import * as Sentry from '@sentry/nextjs';

import { IS_API_ERROR_SINGLE_READ_ENABLED } from '../static/feature-flags';


/** Contexto de diagnóstico comum a qualquer falha da API. */
type CustomErrorContext = {
  endpoint?: string;
  /** Trecho do corpo quando ele não é JSON (página do gateway ou debug do Django). */
  content?: string[];
  useragent?: string;
};

export type CustomErrorArgs =
  | ({ status: number; data: unknown } & CustomErrorContext)
  | ({ status: number; message: string } & CustomErrorContext);

export class CustomError {
  status: number;
  data?: unknown;
  message?: string;
  endpoint?: string;
  content?: string[];
  useragent?: string;

  constructor(args: CustomErrorArgs) {
    this.status = args.status;

    // A divisão passa a ser por FAIXA e não por `=== 500`: um 502/503/504 não traz
    // payload utilizável, tal como um 500, e com o teste antigo (`!== 500`) caía no
    // ramo do `data` — ficava com `data` undefined e perdia a `message` que lhe era
    // passada. Com a flag desligada volta o teste antigo, tal e qual.
    const keepsData = IS_API_ERROR_SINGLE_READ_ENABLED
      ? args.status < 500
      : args.status !== 500;

    if (keepsData) {
      this.data = 'data' in args ? args.data : undefined;
    } else {
      this.message = 'message' in args ? args.message : undefined;
    }

    // Estes três eram aceites no construtor e descartados, pelo que o evento no Sentry
    // chegava sem endpoint nenhum. Ficam atrás da flag só para que `false` reponha o
    // comportamento anterior exato — guardá-los não afeta nenhum consumidor, porque
    // ninguém os lê fora do Sentry.
    if (IS_API_ERROR_SINGLE_READ_ENABLED) {
      this.endpoint = args.endpoint;
      this.content = args.content;
      this.useragent = args.useragent;
    }
  }
}

/**
 * Status HTTP no erro atirado pelos serviços que não passam pelo `wrapFetch`.
 * Não enumerável: os consumidores percorrem `Object.keys(error)` para pintar
 * erros por campo e um `status` visível viraria um `setError` inexistente.
 */
export const withApiStatus = <T>(payload: T, status: number): T => {
  if (typeof payload !== 'object' || payload === null) return payload;
  // Redefinir um `status` do próprio corpo escondê-lo-ia do `Object.keys`.
  if (Object.prototype.hasOwnProperty.call(payload, 'status')) return payload;

  return Object.defineProperty(payload, 'status', {
    value: status,
    enumerable: false,
  });
};

export const getApiErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const { status } = error as { status?: unknown };
  return typeof status === 'number' ? status : undefined;
};

/** Corpo guardado para diagnóstico quando não é JSON. O corpo inteiro pode ter centenas de kB. */
const ERROR_BODY_SNIPPET = 800;

/**
 * Resumo legível de um corpo de erro que não é JSON.
 *
 * Prefere o bloco `<div id="summary">` da página de debug do Django, que é onde está a
 * exceção real; se não existir (página do nginx, do CloudFront), guarda um trecho
 * limitado.
 */
const summariseErrorBody = (body: string): string[] | undefined => {
  const djangoSummary = body.match(/<div id="summary">[\S\s]*?(?=<\/div>)/gm);
  if (djangoSummary) return djangoSummary;

  const snippet = body.trim().slice(0, ERROR_BODY_SNIPPET);
  return snippet ? [snippet] : undefined;
};

/** Recebe o corpo já lido — ler outra vez do `Response` é o que rebentava. */
const logError = (response: Response, body: string) => {
  console.log({
    date: new Date().toISOString(),
    time: new Date().getTime(),
    status: response.status,
    url: response.url,
    headers: response.headers,
    body,
  });
};

/** Logger anterior, mantido tal e qual para o ramo legado (lê de um `clone()`). */
const logLegacyError = async (response: Response) => {
  try {
    const requestBody = await response.text();

    console.log({
      date: new Date().toISOString(),
      time: new Date().getTime(),
      status: response.status,
      url: response.url,
      headers: response.headers,
      body: requestBody,
    });
  } catch (e) {
    console.log({
      date: new Date().toISOString(),
      time: new Date().getTime(),
      status: response.status,
      url: response.url,
      headers: response.headers,
      body: 'response.text() failed',
    });
  }
};

type HeaderReader = (key: string) => string | undefined;

/**
 * Ramo de erro ANTERIOR, mantido verbatim para o caso de ser preciso reverter
 * (`IS_API_ERROR_SINGLE_READ_ENABLED = false`). Não corrigir bugs aqui: o valor deste
 * bloco é ser exatamente o que estava em produção. Sabe-se que:
 *
 *  - nos 500 lê o corpo duas vezes (`text()` e depois `json()`), o que atira
 *    `TypeError: Body is unusable` e substitui o erro real da API por essa mensagem;
 *  - força `status: 500` sempre que o corpo não é JSON, pelo que um 404 ou 301 do
 *    gateway nunca chega às páginas com o seu status.
 */
const throwLegacyApiError = async (
  response: Response,
  getHeader: HeaderReader
): Promise<never> => {
  let requestBody: string | string[] | undefined = undefined;
  let requestBodyStriped: string[] | null = null;

  logLegacyError(response.clone());

  if (response.status === 500) {
    requestBody = await response.text();
    requestBodyStriped = requestBody.match(
      /<div id="summary">[\S\s]*?(?=<\/div>)/gm
    );
  }

  try {
    const apiError = await response.json();

    const theError = new CustomError({
      status: response.status,
      endpoint: response.url,
      content: requestBodyStriped ? (requestBodyStriped as string[]) : undefined,
      useragent: getHeader('user-agent'),
      data: apiError,
    });

    if (response.status === 500) {
      const scope = new Sentry.Scope();
      scope.setExtra('Endpoint', response.url);
      scope.setExtra('API error summary', requestBodyStriped);
      Sentry.captureException(theError, scope);
    }

    throw theError;
  } catch (e) {
    if (e instanceof CustomError) {
      throw e;
    }

    const theError = new CustomError({
      status: 500,
      endpoint: response.url,
      content: requestBodyStriped ? (requestBodyStriped as string[]) : undefined,
      useragent: getHeader('user-agent'),
      message: e.message,
    });

    if (response.status === 500) {
      const scope = new Sentry.Scope();
      scope.setExtra('Endpoint', response.url);
      scope.setExtra('API error summary', requestBodyStriped);
      Sentry.captureException(theError, scope);
    }

    throw theError;
  }
};

/**
 * Ramo de erro atual: UMA leitura do corpo, e nenhum parse nos 5xx.
 *
 * A leitura única é o que evita o `TypeError: Body is unusable` — `text()` seguido de
 * `json()` no mesmo `Response` é ilegal e destruía o erro real da API.
 */
const throwApiError = async (
  response: Response,
  getHeader: HeaderReader
): Promise<never> => {
  const body = await response.text();

  logError(response, body);

  const context = {
    endpoint: response.url,
    useragent: getHeader('user-agent'),
  };

  // 5xx: NÃO se tenta interpretar o corpo como JSON.
  //
  // Não há lá nada para a aplicação: nenhum consumidor lê `data` acima de 500 (as
  // páginas ramificam em 301/404/401/403 e re-lançam o resto), o corpo é tipicamente
  // HTML — página do gateway ou debug do Django — e o próprio `CustomError` descarta o
  // `data` nesta faixa. O parse era trabalho morto que ainda consumia o corpo.
  if (response.status >= 500) {
    const content = summariseErrorBody(body);

    const theError = new CustomError({
      ...context,
      status: response.status,
      message: content
        ? content.join('\n')
        : `API responded ${response.status} with an empty body`,
      content,
    });

    // O gate fica em `=== 500` DE PROPÓSITO: alargá-lo a `>= 500` acrescentaria ao
    // Sentry os 502/504, que hoje não são capturados aqui.
    if (response.status === 500) {
      const scope = new Sentry.Scope();
      scope.setExtra('Endpoint', response.url);
      scope.setExtra('API error summary', content);
      Sentry.captureException(theError, scope);
    }

    throw theError;
  }

  // Abaixo de 500 o payload interessa: é o que alimenta os redirects 301
  // (`category` / `part` / `model` / `product`) e o `detail` dos 404.
  let data: unknown;
  try {
    data = body ? JSON.parse(body) : undefined;
  } catch {
    // Corpo não-JSON num 4xx (uma página de erro do nginx, por exemplo). Fica sem
    // payload, mas o STATUS REAL segue intacto para a página decidir — 404 →
    // `notFound`, 301 → redirect. No ramo legado o parse falhado forçava `status: 500`
    // e a página caía no `throw e`, ou seja numa página de erro e num evento no Sentry.
    data = undefined;
  }

  throw new CustomError({
    ...context,
    status: response.status,
    data,
    content: data === undefined ? summariseErrorBody(body) : undefined,
  });
};

export const wrapFetch = async <T>(
  request: Promise<Response>,
  options?: Parameters<typeof fetch>[1]
): Promise<T> => {
  const getHeader = (key: string) => {
    if (!options?.headers) return undefined;
    if (typeof (options.headers as any).get === 'function') {
      return (options.headers as any).get(key);
    }
    if (Array.isArray(options.headers)) {
      return options.headers.find(([k]) => k.toLowerCase() === key.toLowerCase())?.[1];
    }
    const headersRecord = options.headers as Record<string, string>;
    const matchedKey = Object.keys(headersRecord).find(k => k.toLowerCase() === key.toLowerCase());
    return matchedKey ? headersRecord[matchedKey] : undefined;
  };

  const response = await request;

  if (!response.ok) {
    // Duas implementações a coexistir de propósito — ver
    // `IS_API_ERROR_SINGLE_READ_ENABLED` em `static/feature-flags.ts` para o que cada
    // valor faz. Ambas atiram, por isso este `await` nunca resolve.
    await (IS_API_ERROR_SINGLE_READ_ENABLED
      ? throwApiError(response, getHeader)
      : throwLegacyApiError(response, getHeader));
  }
  return response.status !== 204 ? response.json() : true;
};

export const fechFromBPartsApi = <T>(
  target: Parameters<typeof fetch>[0],
  options?: Parameters<typeof fetch>[1],
  internalOptions?: {
    enableCustomErrorWrapper?: boolean;
    enableHostRemoval?: boolean;
  }
): Promise<T> => {
  // api target configuration and remove cors from dev mode
  let newTarget = `${process.env.API_HOST_SHOP || '/api'}${target}`;

  let isAdvancedView = false;
  const isServiceBox = process.env.NEXT_PUBLIC_TENANT === '5';
  
  if (!isServiceBox) {
    // 1. Procurar Cookie do PRO View no decorrer do cliente (Browser)
    if (typeof document !== 'undefined') {
      isAdvancedView = document.cookie.includes('_advanced_view_active=1');
    } 
    // 2. Procurar Cookie do PRO View no decorrer do servidor (Node - SSR) através dos headers encaminhados
    else if (options?.headers) {
      let cookieStr = '';
      if (options.headers instanceof Headers || typeof (options.headers as any).get === 'function') {
        cookieStr = (options.headers as any).get('cookie') || '';
      } else if (Array.isArray(options.headers)) {
        const cookieTuple = options.headers.find(([k]) => k.toLowerCase() === 'cookie');
        if (cookieTuple) cookieStr = cookieTuple[1] || '';
      } else {
        const headersRecord = options.headers as Record<string, string>;
        const cookieKey = Object.keys(headersRecord).find((k) => k.toLowerCase() === 'cookie');
        if (cookieKey) cookieStr = headersRecord[cookieKey] || '';
      }
      isAdvancedView = cookieStr.includes('_advanced_view_active=1');
    }
  }

  // 3. Acrescentar Query Param caso as condições se reúnam
  if (isAdvancedView) {
    try {
      const isAbsolute = newTarget.startsWith('http://') || newTarget.startsWith('https://');
      const urlObj = new URL(newTarget, isAbsolute ? undefined : 'http://localhost');
      
      if (!urlObj.searchParams.has('is_advanced_view')) {
        urlObj.searchParams.append('is_advanced_view', 'true');
      }
      
      newTarget = isAbsolute 
        ? urlObj.toString() 
        : urlObj.pathname + urlObj.search + urlObj.hash;
    } catch (e) {
      if (!newTarget.includes('is_advanced_view=')) {
        newTarget += newTarget.includes('?') ? '&is_advanced_view=true' : '?is_advanced_view=true';
      }
    }
  }

  const { enableCustomErrorWrapper = false, enableHostRemoval = true } =
    internalOptions || {};

  // removing hosts due to next js server host injection
  if (enableHostRemoval && options?.headers) {
    if (
      options.headers.delete &&
      typeof options.headers.delete === 'function'
    ) {
      options.headers.delete('host');
    } else if (options.headers.host) {
      delete options?.headers.host;
    }
  }

  const tenant = process.env.NEXT_PUBLIC_TENANT || '1';

  if (!options) {
    options = {};
  }

  if (options.headers) {
    let hasOrigin = false;
    
    // Suporte para new Headers() ou algo que implemente um método "has"
    if (options.headers instanceof Headers || typeof (options.headers as any).has === 'function') {
      hasOrigin = (options.headers as any).has('b-origin');
      if (!hasOrigin) {
        (options.headers as any).append('b-origin', tenant);
      }
    } 
    // Suporte para payload tipo Array [string, string][]
    else if (Array.isArray(options.headers)) {
      hasOrigin = options.headers.some(([key]) => key.toLowerCase() === 'b-origin');
      if (!hasOrigin) {
        options.headers.push(['b-origin', tenant]);
      }
    } 
    // Suporte para Record<string, string> tradicional
    else {
      const headersRecord = options.headers as Record<string, string>;
      hasOrigin = Object.keys(headersRecord).some(k => k.toLowerCase() === 'b-origin');
      if (!hasOrigin) {
        headersRecord['b-origin'] = tenant;
      }
    }
  } else {
    options.headers = new Headers({
      'b-origin': tenant,
    });
  }

  if (enableCustomErrorWrapper) {
    return wrapFetch<T>(fetch(newTarget, options), options);
  }

  return fetch<T>(newTarget, options);
};
